import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Store, Star, ChevronRight, QrCode } from 'lucide-react';
import { API, SMART_QR_CATEGORIES, smartCatName, imgSrc } from './smartqrApi';

export const ArrivalCard = ({ b }) => (
  <Link to={`/b/${b.public_slug}`} className="group bg-white rounded-2xl border border-slate-200 p-3 flex items-center gap-3 hover:shadow-md hover:border-orange-200 transition-all" data-testid={`arrival-${b.public_slug}`}>
    <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
      {b.logo ? <img src={imgSrc(b.logo)} alt={b.business_name} className="w-full h-full object-cover" /> : <Store className="w-6 h-6 text-slate-400" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <p className="font-bold text-slate-800 text-sm truncate">{b.business_name}</p>
        {b.featured && <span className="inline-flex items-center gap-0.5 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid={`arrival-featured-${b.public_slug}`}><Star className="w-2.5 h-2.5 fill-white" /> Featured</span>}
        {b.is_demo && <span className="bg-slate-800 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" data-testid={`arrival-demo-${b.public_slug}`}>DEMO</span>}
      </div>
      <p className="text-[11px] text-orange-600 font-semibold">{smartCatName(b.category)}</p>
      <p className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" /> {[b.area, b.city].filter(Boolean).join(', ')}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors shrink-0" />
  </Link>
);

export const NewArrivals = ({ city }) => {
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState('');
  const [scope, setScope] = useState('all');

  useEffect(() => {
    const params = new URLSearchParams({ limit: '5' });
    if (category) params.set('category', category);
    const cityParam = city && city !== 'All Cities' ? city : '';
    const run = async () => {
      try {
        if (cityParam) {
          const local = (await axios.get(`${API}/api/smart-qr/businesses/recent?${params}&city=${encodeURIComponent(cityParam)}`)).data;
          if (local.length) { setItems(local); setScope('city'); return; }
        }
        setItems((await axios.get(`${API}/api/smart-qr/businesses/recent?${params}`)).data);
        setScope('all');
      } catch { setItems([]); }
    };
    run();
  }, [city, category]);

  if (!items || (items.length === 0 && !category)) return null;
  const usedCats = SMART_QR_CATEGORIES.filter((c) => c.id === category || items.some((b) => b.category === c.id));
  const viewAllQs = new URLSearchParams({ ...(scope === 'city' && city ? { city } : {}), ...(category ? { category } : {}) }).toString();
  const viewAllHref = `/smart-business-qr/shops${viewAllQs ? `?${viewAllQs}` : ''}`;

  return (
    <section className="bg-white py-8 border-b border-slate-100" data-testid="new-arrivals-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2"><QrCode className="w-5 h-5 text-orange-500" /> NEW ARRIVALS</h2>
            <p className="text-xs text-slate-500 mt-0.5">{scope === 'city' ? `New shops on JustOffs in ${city}` : 'New shops with a JustOffs Smart Business QR'}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to={viewAllHref} className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold px-3.5 py-2 rounded-full transition-colors whitespace-nowrap" data-testid="arrivals-view-all-link">View All <ChevronRight className="w-3.5 h-3.5" /></Link>
            <Link to="/smart-business-qr" className="hidden sm:inline text-xs font-semibold text-orange-600 hover:text-orange-700 whitespace-nowrap" data-testid="arrivals-get-qr-link">Add your shop →</Link>
          </div>
        </div>
        {usedCats.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4 scrollbar-hide" data-testid="arrivals-category-filter">
            <button onClick={() => setCategory('')} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${!category ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`} data-testid="arrivals-cat-all">All</button>
            {usedCats.map((c) => <button key={c.id} onClick={() => setCategory(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${category === c.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`} data-testid={`arrivals-cat-${c.id}`}>{c.name}</button>)}
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center bg-slate-50 rounded-xl" data-testid="arrivals-empty">No shops in this category yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{items.slice(0, 5).map((b) => <ArrivalCard key={b.public_slug} b={b} />)}</div>
        )}
      </div>
    </section>
  );
};
